import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  getDocs,
  query,
  where
} from 'firebase/firestore';
import Modal from 'react-modal';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from '../images/shop_logo.png';
import { SHOP_NAME } from "../Variables.js";
import Loader from "./Loader";
import SuccessModal from './SuccessModal';

Modal.setAppElement('#root');

// Number formatter for amounts with Naira (₦)
const formatAmount = (amount) => {
    return `N${amount.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    })}`;
};

const formatAmount1 = (amount) => {
  return `₦ ${amount.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
  })}`;
};

// Date formatter for DD MMM YYYY (e.g., 07 Oct 2025)
const dateFormatter = new Intl.DateTimeFormat('en-NG', {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
});

// Date formatter for filename (YYYY-MM-DD)
const formatDateForFilename = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Date-time formatter for header (DD MMM YYYY, HH:MM AM/PM)
const formatDateTime = (date) => {
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');
};

// Generate 8-character journalId from Firestore ID
const generateJournalId = (firestoreId) => {
  return firestoreId.substring(0, 8);
};

const GeneralLedger = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [ledgerTransactions, setLedgerTransactions] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [filter, setFilter] = useState('');
  const [isFetchingLedger, setIsFetchingLedger] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const accountsCollectionRef = collection(db, "accounts");
  const journalEntriesCollectionRef = collection(db, "journalEntries");
  const storeName = `${SHOP_NAME}`;

  useEffect(() => {
    if (!user || !user.uid || !userData) {
      console.log("Waiting for user authentication or userData...");
      setLoading(true);
      return;
    }
    setLoading(false);
    fetchAccounts();
  }, [user, userData]);

  const fetchAccounts = async () => {
    if (!user?.uid) {
      console.error("No user ID available for fetching accounts");
      setError("User not authenticated");
      setShowModal(true);
      return;
    }
    try {
      const q = query(accountsCollectionRef);
      const data = await getDocs(q);
      const accountList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setAccounts(accountList);
      if (accountList.length === 0) {
        console.log("No accounts found for user:", user.uid);
        setError("No accounts found");
        setShowModal(true);
      }
    } catch (error) {
      console.error("Failed to fetch accounts:", error.message, error.code);
      setError(`Failed to fetch accounts: ${error.message} (${error.code})`);
      setShowModal(true);
    }
  };

  const fetchLedgerForAccount = async (accountId) => {
    if (!user?.uid) {
      console.error("No user ID available for fetching ledger");
      setError("User not authenticated");
      setShowModal(true);
      return;
    }
    setIsFetchingLedger(true);
    try {
      const q = query(journalEntriesCollectionRef);
      const data = await getDocs(q);
      const allEntries = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setJournalEntries(allEntries); // Store all entries for quick access in handleViewDetails
      const selectedAccountData = accounts.find(acc => acc.id === accountId);
      const accountName = selectedAccountData ? selectedAccountData.name : 'Unknown';
      const transactions = [];
      allEntries.forEach(entry => {
        entry.lines.forEach(line => {
          if (line.accountName === accountName || (line.accountId && line.accountId === accountId)) {
            const debit = Number(line.debit) || 0;
            const credit = Number(line.credit) || 0;
            transactions.push({
              date: entry.date,
              description: entry.description,
              debit: debit,
              credit: credit,
              journalId: entry.journalId || generateJournalId(entry.id),
              userName: entry.userName || 'Unknown'
            });
          }
        });
      });
      transactions.sort((a, b) => new Date(b.date) - new Date(a.date));
      setLedgerTransactions(transactions);
      if (transactions.length === 0) {
        console.log("No transactions found for account:", accountName);
        setError(`No transactions found for ${accountName}`);
        setShowModal(true);
      }
    } catch (error) {
      console.error("Failed to fetch ledger:", error.message, error.code);
      setError(`Failed to fetch ledger: ${error.message} (${error.code})`);
      setShowModal(true);
    } finally {
      setIsFetchingLedger(false);
    }
  };

  const handleSelectAccount = (accountId) => {
    setSelectedAccount(accountId);
    setFilter('');
    if (accountId) {
      fetchLedgerForAccount(accountId);
    } else {
      setLedgerTransactions([]);
      setJournalEntries([]);
    }
  };

  const filteredTransactions = useMemo(() => {
    return ledgerTransactions.filter(tx => 
      !filter || 
      dateFormatter.format(new Date(tx.date)).replace(/,/g, '').toLowerCase().includes(filter.toLowerCase()) || 
      tx.date.includes(filter) || 
      tx.journalId.toLowerCase().includes(filter.toLowerCase())
    );
  }, [ledgerTransactions, filter]);

  const handleViewDetails = (journalId) => {
    setIsLoadingDetails(true);
    const entry = journalEntries.find(e => (e.journalId || generateJournalId(e.id)) === journalId);
    if (entry) {
      setSelectedEntry({ ...entry });
      setIsDetailModalOpen(true);
      setIsLoadingDetails(false);
    } else {
      setError("Journal entry not found");
      setShowModal(true);
      setIsLoadingDetails(false);
    }
  };

  const validateDateRange = () => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates");
      setShowModal(true);
      return false;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError("Start date cannot be after end date");
      setShowModal(true);
      return false;
    }
    return true;
  };

  const generateLedgerPDF = () => {
    if (!validateDateRange()) return;

    if (!selectedAccount || filteredTransactions.length === 0) {
      setError("Select an account with transactions to generate PDF");
      setShowModal(true);
      return;
    }

    const dateFilteredTransactions = filteredTransactions.filter(tx => {
      const txDate = new Date(tx.date);
      return txDate >= new Date(startDate) && txDate <= new Date(endDate);
    });

    if (dateFilteredTransactions.length === 0) {
      setError("No transactions found for the selected date range");
      setShowModal(true);
      return;
    }

    const doc = new jsPDF();
    const accountName = accounts.find(acc => acc.id === selectedAccount)?.name || 'Unknown';
    const fullName = userData?.firstName && userData?.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : user?.displayName || "Unknown";

    let currentY = 10;
    doc.addImage(Logo, "PNG", 14, currentY, 15, 15);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(storeName, 35, currentY + 5);
    doc.setFontSize(16);
    doc.setFont("helvetica", "normal");
    doc.text(`General Ledger Report - ${accountName}`, 35, currentY + 13);
    doc.setFontSize(10);
    doc.text(`Generated by: ${fullName}`, 35, currentY + 18);
    doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, currentY + 23);
    doc.text(`Period: ${dateFormatter.format(new Date(startDate))} to ${dateFormatter.format(new Date(endDate))}`, 35, currentY + 28);
    currentY += 33;

    let runningBalance = 0;
    const tableData = dateFilteredTransactions.map((tx, index) => {
      const debit = Number(tx.debit) || 0;
      const credit = Number(tx.credit) || 0;
      runningBalance += debit - credit;
      return [
        (index + 1).toString(),
        dateFormatter.format(new Date(tx.date)),
        tx.description,
        debit > 0 ? formatAmount(debit) : '-',
        credit > 0 ? formatAmount(credit) : '-',
        formatAmount(runningBalance)
      ];
    });

    autoTable(doc, {
      startY: currentY,
      head: [['S/N', 'Date', 'Description', 'Debit', 'Credit', 'Balance']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [41, 128, 185] },
      bodyStyles: { textColor: [0, 0, 0] },
      margin: { left: 14, right: 10 },
      styles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 25 },
        2: { cellWidth: 60 },
        3: { cellWidth: 25 },
        4: { cellWidth: 25 },
        5: { cellWidth: 25 }
      },
      didDrawPage: (data) => {
        currentY = data.table.finalY || data.cursor.y;
        if (data.pageNumber < doc.internal.getNumberOfPages()) {
          doc.setPage(data.pageNumber + 1);
          doc.addImage(Logo, "PNG", 14, 10, 15, 15);
          doc.setFontSize(16);
          doc.setFont("helvetica", "bold");
          doc.text(storeName, 35, 15);
          doc.setFontSize(16);
          doc.setFont("helvetica", "normal");
          doc.text(`General Ledger Report - ${accountName}`, 35, 23);
          doc.setFontSize(10);
          doc.text(`Generated by: ${fullName}`, 35, 28);
          doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, 33);
          doc.text(`Period: ${dateFormatter.format(new Date(startDate))} to ${dateFormatter.format(new Date(endDate))}`, 35, 38);
          currentY = 44;
        }
      }
    });

    currentY += 10;
    doc.setFontSize(12);
    doc.text(`Ending Balance: ${formatAmount(runningBalance)}`, 14, currentY);

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width - 30,
        doc.internal.pageSize.height - 10
      );
    }

    doc.save(`general_ledger_${accountName}_${formatDateForFilename(new Date(startDate))}_to_${formatDateForFilename(new Date(endDate))}.pdf`);
    setIsDateRangeModalOpen(false);
    setStartDate('');
    setEndDate('');
  };

  if (loading) return <Loader text={"LOADING"} />;
  if (!userData) return <Loader text={"READING"} />;

  const allowedRoles = ['Accountant', 'Admin', 'Supreme'];

  return (
    <>
      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">General Ledger</h2>
            <Link to="#" className="home-link" onClick={() => selectedAccount && fetchLedgerForAccount(selectedAccount)}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed">            
            <select
              className="input-field1"
              style={{marginTop: '20px', marginBottom: '-10px'}}
              value={selectedAccount || ''}
              onChange={(e) => handleSelectAccount(e.target.value)}
            >
              <option value="">Select Account</option>
              {accounts.map(acc => (
                <option key={acc.id} value={acc.id}>{acc.name}</option>
              ))}
            </select>

            {selectedAccount && (
              <>
                <div className="row justify-center align-center gap-15 add-buttons">
                  <button className="login-button login-button-d caps" onClick={() => setIsDateRangeModalOpen(true)}>Download PDF</button>
                </div>
                <input
                  type="text"
                  className="input-field1"
                  placeholder="Filter by date (DD MMM YYYY or YYYY-MM-DD) or journal ID"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
                <div className="scroll-fixed scroll-fixed1">
                  <div className="class-list class-list-d scrollable">
                    {isFetchingLedger ? (
                      <p className="loading-text">Loading ledger...</p>
                    ) : filteredTransactions.length === 0 ? (
                      <p className="loading-text">No transactions found for the selected account.</p>
                    ) : (
                      filteredTransactions.map((tx, index) => (
                        <div key={index} className="class-item">
                          <div className="column gap-1">
                            <p>
                              <strong>{tx.description}</strong><br />
                              <span>Journal ID: {tx.journalId}</span><br />
                              <span>Date: {dateFormatter.format(new Date(tx.date))}</span><br />
                              <span>Debit: {formatAmount1(tx.debit)}</span><br />
                              <span>Credit: {formatAmount1(tx.credit)}</span><br />
                              <span>By: {tx.userName}</span>
                            </p>                        
                          </div>
                          <button className="list-btn" onClick={() => handleViewDetails(tx.journalId)} disabled={isLoadingDetails}>
                            {isLoadingDetails ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-eye"></i>}
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <Modal
            isOpen={isDateRangeModalOpen}
            onRequestClose={() => {
              setIsDateRangeModalOpen(false);
              setStartDate('');
              setEndDate('');
            }}
            contentLabel="Select Date Range"
            className="custom-modal"
            overlayClassName="custom-modal-overlay"
          >
            <h2>Select Date Range for PDF</h2>
            <form className="session-form">
              <label className="input-label">Start Date</label>
              <input
                type="date"
                className="input-field"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
              <label className="input-label">End Date</label>
              <input
                type="date"
                className="input-field"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
              />
              <div className="row align-center gap-1">
                <button
                  className="login-button"
                  type="button"
                  onClick={() => {
                    setIsDateRangeModalOpen(false);
                    setStartDate('');
                    setEndDate('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className="login-button"
                  type="button"
                  onClick={generateLedgerPDF}
                >
                  Generate PDF
                </button>
              </div>
            </form>
          </Modal>

          {showModal && (
            <SuccessModal
              icon={error ? "xmark" : "check"}
              iconColor={error ? "red" : "green"}
              message={error}
              onClose={() => {
                setError("");
                setShowModal(false);
              }}
            />
          )}

          <Modal
            isOpen={isDetailModalOpen}
            onRequestClose={() => setIsDetailModalOpen(false)}
            contentLabel="Entry Details"
            className="custom-modal"
            overlayClassName="custom-modal-overlay"
          >
            <h2>Journal Entry Details</h2>
            {isLoadingDetails ? (
              <Loader text="Loading details..." />
            ) : selectedEntry ? (
              <div>
                <p><strong>Journal ID:</strong> {selectedEntry.journalId || 'N/A'}</p>
                <p><strong>Description:</strong> {selectedEntry.description}</p>
                <p><strong>Date:</strong> {dateFormatter.format(new Date(selectedEntry.date))}</p>
                <p><strong>Created By:</strong> {selectedEntry.userName || 'Unknown'}</p>
                <h4>Lines:</h4>
                {selectedEntry.lines.map((line, index) => (
                  <p key={index}>
                    <span>Account: {line.accountName || (line.accountId ? accounts.find(a => a.id === line.accountId)?.name || 'Unknown' : 'Unknown')} </span><br />
                    <span>Debit: {formatAmount1(line.debit)}</span><br />
                    <span>Credit: {formatAmount1(line.credit)}</span>
                  </p>
                ))}
                <button className="login-button" onClick={() => setIsDetailModalOpen(false)}>Close</button>
              </div>
            ) : (
              <p>No details available</p>
            )}
          </Modal>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}
    </>
  );
};

export default GeneralLedger;